// === INPACT:SLUITSTRAAT:BOILERPLATE:WORKFLOW-CONFIG-PAGE:v1 ===

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useConfig,
  updateWorkflowConfig,
  SettingsHeader,
  SettingsFooter,
} from '@sluitstraat/core';

interface WorkflowProduct {
  id: string;
  name: string;
  order: number;
  required?: boolean;
  jsonFormSchema?: any;
  jsonFormUISchema?: any;
}

const WorkflowConfigurationPage: React.FC = () => {
  const { config, reloadConfig } = useConfig();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [products, setProducts] = useState<WorkflowProduct[]>(
    config.workflow?.screens || []
  );

  const [selectedProductIndex, setSelectedProductIndex] = useState<number | null>(null);
  const [importedFormsFile, setImportedFormsFile] = useState<string | null>(null);

  const handleBulkFormsImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = JSON.parse(e.target?.result as string);

        let importedProducts: WorkflowProduct[] = [];
        const currentProductCount = products.length;

        // Format 1: Multi-product workflow format { products: [...] }
        if (jsonContent.products && Array.isArray(jsonContent.products)) {
          importedProducts = jsonContent.products.map((product: any, index: number) => ({
            id: product.id || product.name?.toLowerCase().replace(/\s+/g, '-') || `product-${currentProductCount + index + 1}`,
            name: product.name || `Product ${currentProductCount + index + 1}`,
            order: currentProductCount + index + 1,
            required: product.required || false,
            jsonFormSchema: product.jsonFormSchema || product.jsonSchema || product.schema,
            jsonFormUISchema: product.jsonFormUISchema || product.uiSchema,
          }));
        }
        // Format 2: Single product format { jsonSchema: {...}, uiSchema: {...} }
        else if (jsonContent.jsonSchema) {
          const productName = file.name.replace('.json', '');
          importedProducts = [{
            id: productName.toLowerCase().replace(/\s+/g, '-'),
            name: productName,
            order: currentProductCount + 1,
            required: false,
            jsonFormSchema: jsonContent.jsonSchema,
            jsonFormUISchema: jsonContent.uiSchema,
          }];
        }
        // Format 3: Legacy screens format { screens: [...] } - convert to products
        else if (jsonContent.screens && Array.isArray(jsonContent.screens)) {
          importedProducts = jsonContent.screens.map((screen: any, index: number) => ({
            id: screen.id || screen.name?.toLowerCase().replace(/\s+/g, '-') || `product-${currentProductCount + index + 1}`,
            name: screen.name || `Product ${currentProductCount + index + 1}`,
            order: currentProductCount + index + 1,
            required: screen.required || false,
            jsonFormSchema: screen.jsonFormSchema || screen.schema,
            jsonFormUISchema: screen.jsonFormUISchema || screen.uiSchema,
          }));
        }
        else {
          setNotification({
            type: 'error',
            message: 'Invalid format. Expected: { products: [...] } OR { jsonSchema: {...}, uiSchema: {...} }'
          });
          return;
        }

        // Add to existing products
        setProducts([...products, ...importedProducts]);
        setImportedFormsFile(file.name);
        setNotification({
          type: 'success',
          message: `Successfully imported ${importedProducts.length} product(s) from ${file.name}`
        });
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Invalid JSON file. Please check the file format.'
        });
      }
    };
    reader.readAsText(file);
  };

  const handleSchemaFileUpload = (event: React.ChangeEvent<HTMLInputElement>, productIndex: number, type: 'schema' | 'uischema') => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = JSON.parse(e.target?.result as string);
        const newProducts = [...products];

        if (type === 'schema') {
          newProducts[productIndex].jsonFormSchema = jsonContent;
        } else {
          newProducts[productIndex].jsonFormUISchema = jsonContent;
        }

        setProducts(newProducts);
        setNotification({ type: 'success', message: `${type === 'schema' ? 'Schema' : 'UI Schema'} imported successfully!` });
      } catch (error) {
        setNotification({ type: 'error', message: 'Invalid JSON file. Please check the file format.' });
      }
    };
    reader.readAsText(file);
  };

  const removeProduct = (index: number) => {
    const newProducts = products.filter((_, i) => i !== index);
    // Reorder remaining products
    newProducts.forEach((product, i) => {
      product.order = i + 1;
    });
    setProducts(newProducts);
  };

  const moveProduct = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === products.length - 1)
    ) {
      return;
    }

    const newProducts = [...products];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap
    [newProducts[index], newProducts[targetIndex]] = [newProducts[targetIndex], newProducts[index]];

    // Update order
    newProducts.forEach((product, i) => {
      product.order = i + 1;
    });

    setProducts(newProducts);
  };

  const toggleRequired = (index: number) => {
    const newProducts = [...products];
    newProducts[index].required = !newProducts[index].required;
    setProducts(newProducts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (products.length === 0) {
      setNotification({ type: 'error', message: 'Please import at least one product' });
      return;
    }

    setIsSubmitting(true);
    setNotification(null);

    try {
      // Save as screens for backward compatibility with existing config structure
      const success = await updateWorkflowConfig({ screens: products });

      if (success) {
        setNotification({ type: 'success', message: 'Product configuration saved successfully!' });

        // Reload the config to reflect the changes
        try {
          await reloadConfig();
        } catch (configError) {
          // Continue with navigation even if reload fails
        }

        // Navigate back to setup page
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 1500);
      } else {
        setNotification({ type: 'error', message: 'Failed to save product configuration. Please try again.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'An error occurred while saving. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <>
      <SettingsHeader />
      <div style={styles.container}>
        <div style={styles.content}>
          <header style={styles.header}>
            <button onClick={handleCancel} style={styles.backButton}>
              ← Back to Setup
            </button>
            <h1 style={styles.title}>Product Configuration</h1>
            <p style={styles.subtitle}>Import insurance product JSONForms data that will be split into workflow screens</p>
          </header>

        {notification && (
          <div style={{
            ...styles.notification,
            ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
          }}>
            <span style={styles.notificationIcon}>
              {notification.type === 'success' ? '✓' : '⚠'}
            </span>
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* JSON Forms Import Section */}
          <div style={styles.importSection}>
            <div style={styles.importHeader}>
              <div>
                <h2 style={styles.sectionTitle}>Import Insurance Products</h2>
                <p style={styles.importDescription}>
                  Import JSONForms product data files. These products will later be analyzed and split into appropriate workflow screens.
                </p>
              </div>
            </div>

            <div style={styles.importContainer}>
              <div style={styles.importBox}>
                <div style={styles.importBoxIcon}>📦</div>
                <div style={styles.importBoxContent}>
                  <h3 style={styles.importBoxTitle}>Import Product JSONForms File</h3>
                  <p style={styles.importBoxDescription}>
                    Upload JSON files containing insurance product definitions (e.g., Woonhuis, Inboedel, etc.)
                  </p>
                  <div style={styles.importActions}>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleBulkFormsImport}
                      style={styles.fileInput}
                      id="bulk-forms-import"
                    />
                    <label htmlFor="bulk-forms-import" style={styles.importButton}>
                      📁 Choose Product File
                    </label>
                    {importedFormsFile && (
                      <span style={styles.importedFileName}>
                        ✓ {importedFormsFile}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={styles.importInfo}>
                <span style={styles.importInfoIcon}>💡</span>
                <div style={styles.importInfoText}>
                  <strong>Supported Formats:</strong>
                  <div style={{ marginTop: '8px' }}>
                    1. Single product: <code>{`{ "jsonSchema": {...}, "uiSchema": {...} }`}</code>
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    2. Multiple products: <code>{`{ "products": [...] }`}</code>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                    Examples: <code>Woonhuis.json</code>, <code>Inboedel.json</code>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>
                    Note: Products contain large datasets that will be intelligently split into multiple workflow screens during the build process.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              Imported Products ({products.length})
            </h2>

            {products.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyStateIcon}>📦</span>
                <p style={styles.emptyStateText}>No products imported yet</p>
                <p style={styles.emptyStateHint}>Import product JSONForms files above to configure your workflow</p>
              </div>
            ) : (
              <div style={styles.productList}>
                {products.map((product, index) => (
                  <div key={product.id} style={styles.productItemContainer}>
                    <div style={styles.productItem}>
                      <div style={styles.productItemLeft}>
                        <span style={styles.productOrder}>{product.order}</span>
                        <div style={styles.productInfo}>
                          <h3 style={styles.productName}>{product.name}</h3>
                          <p style={styles.productId}>ID: {product.id}</p>
                        </div>
                      </div>

                      <div style={styles.productItemRight}>
                        <label style={styles.requiredToggle}>
                          <input
                            type="checkbox"
                            checked={product.required || false}
                            onChange={() => toggleRequired(index)}
                            style={styles.checkbox}
                          />
                          <span style={styles.requiredLabel}>Required</span>
                        </label>

                        <div style={styles.productActions}>
                          <button
                            type="button"
                            onClick={() => moveProduct(index, 'up')}
                            disabled={index === 0}
                            style={{
                              ...styles.actionButton,
                              ...(index === 0 ? styles.actionButtonDisabled : {})
                            }}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveProduct(index, 'down')}
                            disabled={index === products.length - 1}
                            style={{
                              ...styles.actionButton,
                              ...(index === products.length - 1 ? styles.actionButtonDisabled : {})
                            }}
                            title="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeProduct(index)}
                            style={styles.deleteButton}
                            title="Remove"
                          >
                            ×
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedProductIndex(selectedProductIndex === index ? null : index)}
                            style={styles.configButton}
                            title="View/Edit JSON Forms"
                          >
                            📝
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* JSON Forms Configuration Section */}
                    {selectedProductIndex === index && (
                      <div style={styles.jsonFormsConfig}>
                        <h4 style={styles.jsonFormsTitle}>JSON Forms Configuration</h4>
                        <p style={styles.jsonFormsDescription}>
                          View or update JSON Schema files for this product (powered by jsonforms.io)
                        </p>

                        <div style={styles.schemaUploadContainer}>
                          <div style={styles.schemaUploadItem}>
                            <label style={styles.schemaUploadLabel}>
                              <span style={styles.schemaUploadLabelText}>JSON Schema</span>
                              <span style={styles.schemaUploadHint}>Defines form structure and validation</span>
                            </label>
                            <div style={styles.schemaUploadActions}>
                              <input
                                type="file"
                                accept=".json"
                                onChange={(e) => handleSchemaFileUpload(e, index, 'schema')}
                                style={styles.fileInput}
                                id={`schema-upload-${index}`}
                              />
                              <label htmlFor={`schema-upload-${index}`} style={styles.uploadButton}>
                                {product.jsonFormSchema ? '✓ Schema Loaded' : '📁 Import Schema'}
                              </label>
                              {product.jsonFormSchema && (
                                <span style={styles.schemaStatus}>
                                  {Object.keys(product.jsonFormSchema.properties || {}).length} fields
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={styles.schemaUploadItem}>
                            <label style={styles.schemaUploadLabel}>
                              <span style={styles.schemaUploadLabelText}>UI Schema (Optional)</span>
                              <span style={styles.schemaUploadHint}>Customizes form layout and appearance</span>
                            </label>
                            <div style={styles.schemaUploadActions}>
                              <input
                                type="file"
                                accept=".json"
                                onChange={(e) => handleSchemaFileUpload(e, index, 'uischema')}
                                style={styles.fileInput}
                                id={`uischema-upload-${index}`}
                              />
                              <label htmlFor={`uischema-upload-${index}`} style={styles.uploadButton}>
                                {product.jsonFormUISchema ? '✓ UI Schema Loaded' : '📁 Import UI Schema'}
                              </label>
                            </div>
                          </div>
                        </div>

                        <div style={styles.jsonFormsInfo}>
                          <span style={styles.jsonFormsInfoIcon}>💡</span>
                          <div style={styles.jsonFormsInfoText}>
                            <strong>Tip:</strong> You can use the example schemas from <code>docs/examples/jsonforms-schemas/</code> or create your own following the JSON Schema standard.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.infoBox}>
            <div style={styles.infoBoxIcon}>ℹ️</div>
            <div>
              <h3 style={styles.infoBoxTitle}>Product Configuration Information</h3>
              <ul style={styles.infoBoxList}>
                <li><strong>Products vs Screens:</strong> Products are large insurance datasets (e.g., Woonhuis, Inboedel) that contain extensive form fields</li>
                <li><strong>Intelligent Screen Generation:</strong> During the build process, these products will be analyzed and split into multiple logical workflow screens</li>
                <li><strong>Avoid Duplication:</strong> This approach prevents duplicate fields across products and creates a better user experience</li>
                <li><strong>Ordering:</strong> Use ↑ ↓ to set the priority order of products</li>
                <li><strong>Required Flag:</strong> Mark products as required if they must be completed in the workflow</li>
                <li><strong>Flexible Import:</strong> Import individual products or bundles of multiple products at once</li>
              </ul>
            </div>
          </div>

          <div style={styles.actionBar}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.saveButton,
                ...(isSubmitting ? styles.saveButtonDisabled : {})
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Product Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <SettingsFooter />
    </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    padding: '32px 20px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#6b7280',
  },
  notification: {
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '600' as const,
  },
  notificationSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '2px solid #10b981',
  },
  notificationError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #ef4444',
  },
  notificationIcon: {
    fontSize: '18px',
    fontWeight: '700' as const,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '32px',
  },
  importSection: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '2px solid #e5e7eb',
  },
  importHeader: {
    marginBottom: '20px',
  },
  importDescription: {
    margin: '8px 0 0 0',
    fontSize: '14px',
    color: '#6b7280',
  },
  importContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  importBox: {
    display: 'flex',
    gap: '20px',
    padding: '24px',
    backgroundColor: '#f0f9ff',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
  },
  importBoxIcon: {
    fontSize: '48px',
    flexShrink: 0,
  },
  importBoxContent: {
    flex: 1,
  },
  importBoxTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1e40af',
  },
  importBoxDescription: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: '1.5',
  },
  importActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  importButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  importedFileName: {
    fontSize: '14px',
    color: '#10b981',
    fontWeight: '600' as const,
    padding: '8px 12px',
    backgroundColor: '#d1fae5',
    borderRadius: '6px',
  },
  importInfo: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fef3c7',
    borderRadius: '6px',
  },
  importInfoIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  importInfoText: {
    fontSize: '13px',
    color: '#92400e',
    lineHeight: '1.5',
    flex: 1,
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #e5e7eb',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center' as const,
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    border: '2px dashed #d1d5db',
  },
  emptyStateIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  emptyStateText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  emptyStateHint: {
    margin: '0',
    fontSize: '14px',
    color: '#6b7280',
  },
  productList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  productItemContainer: {
    backgroundColor: '#F5F5F5',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  productItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
  },
  productItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  productOrder: {
    width: '32px',
    height: '32px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700' as const,
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    margin: '0 0 4px 0',
    fontSize: '15px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  productId: {
    margin: '0',
    fontSize: '12px',
    color: '#9ca3af',
    fontFamily: 'monospace',
  },
  productItemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  requiredToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  requiredLabel: {
    fontSize: '13px',
    fontWeight: '600' as const,
    color: '#6b7280',
  },
  productActions: {
    display: 'flex',
    gap: '6px',
  },
  actionButton: {
    width: '32px',
    height: '32px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '700' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  actionButtonDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  deleteButton: {
    width: '32px',
    height: '32px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: 'none',
    borderRadius: '4px',
    fontSize: '24px',
    fontWeight: '700' as const,
    cursor: 'pointer',
    lineHeight: '1',
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '32px',
    display: 'flex',
    gap: '16px',
  },
  infoBoxIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  infoBoxTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1e40af',
  },
  infoBoxList: {
    margin: '0',
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '24px',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  configButton: {
    width: '32px',
    height: '32px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  jsonFormsConfig: {
    padding: '20px',
    backgroundColor: 'white',
    borderTop: '2px solid #e5e7eb',
  },
  jsonFormsTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  jsonFormsDescription: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5',
  },
  schemaUploadContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    marginBottom: '16px',
  },
  schemaUploadItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px',
    backgroundColor: '#F5F5F5',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  schemaUploadLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    flex: 1,
  },
  schemaUploadLabelText: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  schemaUploadHint: {
    fontSize: '12px',
    color: '#6b7280',
  },
  schemaUploadActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fileInput: {
    display: 'none',
  },
  uploadButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  schemaStatus: {
    fontSize: '12px',
    color: '#10b981',
    fontWeight: '600' as const,
    padding: '4px 8px',
    backgroundColor: '#d1fae5',
    borderRadius: '4px',
  },
  jsonFormsInfo: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fef3c7',
    borderRadius: '6px',
  },
  jsonFormsInfoIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  jsonFormsInfoText: {
    fontSize: '13px',
    color: '#92400e',
    lineHeight: '1.5',
  },
};

export default WorkflowConfigurationPage;
